import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { bimNumberFormatter } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';

import type { CellClassParams, ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

import type { IBimActKc2PriceRowData } from './useBimActKc2PricesGroupedData';

const cellStyle = (cellClassParams: CellClassParams<IBimActKc2PriceRowData>) => {
    if (cellClassParams.data?.isSectionHeader) {
        return { fontWeight: 600, background: 'var(--color-bg-stripe)', color: 'var(--color-typo-primary)' };
    }

    if (cellClassParams.data?.inactiveMtr) {
        return { color: 'var(--color-typo-secondary)', background: 'white', fontWeight: 400 };
    }

    return { background: 'white', color: 'var(--color-typo-primary)', fontWeight: 400 };
};

export const useBimActKc2PricesColumns = (
    filterData: { name: string; code: string }[],
    changeFilterHandler: ({ fieldName, checkedValues }: any) => void,
) =>
    useMemo<ColDef<IBimActKc2PriceRowData>[]>(
        () => [
            {
                headerName: 'Номер',
                children: [
                    {
                        headerName: 'По порядку',
                        field: 'serialNumber',
                        spanHeaderHeight: true,
                        width: 136,
                        headerClass: 'center-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                        cellStyle,
                    },
                    {
                        headerName: 'Позиций по смете',
                        field: 'number',
                        spanHeaderHeight: true,
                        width: 176,
                        headerClass: 'center-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                        cellStyle,
                    },
                ],
            },
            {
                headerName: 'Наименование работ',
                field: 'name',
                width: 340,
                spanHeaderHeight: true,
                cellStyle,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    changeFilterHandler,
                    filterItems: filterData.map((item) => ({ label: item.name })),
                    columns: [
                        {
                            headerName: 'Наименование работ, затрат',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    isClientSideSearch: true,
                    childFieldAccessor: 'label',
                },
            },
            {
                headerName: 'Номер единичной расценки',
                field: 'code',
                width: 340,
                spanHeaderHeight: true,
                cellStyle,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    changeFilterHandler,
                    filterItems: filterData.map((item) => ({ label: item.code })),
                    columns: [
                        {
                            headerName: 'Номер единичной расценки',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    isClientSideSearch: true,
                    childFieldAccessor: 'label',
                },
            },
            {
                headerName: 'Единица измерения',
                field: 'unit',
                width: 180,
                spanHeaderHeight: true,
                cellStyle,
            },
            {
                headerName: 'Выполнено работ',
                children: [
                    {
                        headerName: 'Кол-во на ед./всего',
                        field: 'quantity',
                        width: 200,
                        spanHeaderHeight: true,
                        headerClass: 'center-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: bimNumberFormatter(4),
                        cellStyle,
                    },
                    {
                        headerName: 'Цена за единицу, ₽',
                        field: 'unitCost',
                        width: 200,
                        spanHeaderHeight: true,
                        headerClass: 'center-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: bimNumberFormatter(2),
                        cellStyle,
                    },
                    {
                        headerName: 'Стоимость, ₽',
                        field: 'total',
                        minWidth: 200,
                        flex: 1,
                        spanHeaderHeight: true,
                        headerClass: 'center-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: bimNumberFormatter(2),
                        cellStyle,
                    },
                ],
            },
        ],
        [changeFilterHandler, filterData],
    );
